import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, Pagination, Stack, TextField, Typography
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import AuthenticatedMedia from '../chat/AuthenticatedMedia';
import { libraryMediaSource, listLibraryMedia } from '../../services/mediaLibrary.service';
import { formatBytes } from '../../pages/MediaLibraryPage';

// Flow Builder <-> Media Library (Phase E). Selecting a library file only stores
// the stable reference `media-library:<id>` on the node: nothing is uploaded to
// WhatsApp here. The server re-validates the reference on save and binds the file
// to the flow's own WhatsApp account when the flow actually sends it.

export const LIBRARY_NODE_TYPES = { image_message: 'image', video_message: 'video', audio_message: 'audio', file_document: 'document' };
const PAGE_SIZE = 12;
const TYPE_LABELS = { image: 'images', video: 'videos', audio: 'audio files', document: 'documents' };
// Mirrors the server's WhatsApp media rules; the server re-checks on save and send.
const WHATSAPP_RULES = {
  image: { maxBytes: 5 * 1024 * 1024, mimeTypes: ['image/jpeg', 'image/png'], label: 'JPG or PNG up to 5 MB' },
  video: { maxBytes: 16 * 1024 * 1024, mimeTypes: ['video/mp4', 'video/3gpp'], label: 'MP4 or 3GPP up to 16 MB' },
  audio: { maxBytes: 16 * 1024 * 1024, mimeTypes: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'], label: 'AAC, M4A, MP3, AMR or OGG up to 16 MB' },
  document: {
    maxBytes: 100 * 1024 * 1024,
    mimeTypes: ['application/pdf', 'text/plain', 'text/csv', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    label: 'PDF, Office, text or CSV up to 100 MB'
  }
};
const LIBRARY_FIELDS = ['mediaLibraryRef', 'mediaLibraryItem', 'mediaLibraryBinding'];
const UPLOAD_FIELDS = ['whatsappMediaId', 'mediaId', 'mediaAccountId', 'mediaLocalRef', 'localMediaRef', 'imageUrl', 'mediaUrl', 'fileUrl', 'fileDataBase64', 'mediaDataBase64', 'fileError'];

export function libraryIncompatibility(item, mediaType) {
  const rule = WHATSAPP_RULES[mediaType];
  if (!rule) return 'This node cannot use Media Library files.';
  const group = mediaType === 'audio' ? ['audio', 'voice'] : mediaType === 'document' ? ['document', 'pdf'] : [mediaType];
  if (!group.includes(item?.mediaType)) return `Not ${mediaType === 'image' || mediaType === 'audio' ? 'an' : 'a'} ${mediaType} file.`;
  if (!rule.mimeTypes.includes(String(item.mimeType || '').toLowerCase())) return `WhatsApp accepts ${rule.label}.`;
  if (Number(item.size || 0) > rule.maxBytes) return `WhatsApp accepts ${rule.label}.`;
  return '';
}

// Returns the node config with the library file selected and every previous
// upload/URL/provider source cleared (the user replaced it deliberately).
export function applyLibrarySelection(config, item, nodeType) {
  const next = { ...config };
  [...UPLOAD_FIELDS, 'mediaLibraryBinding'].forEach((field) => { delete next[field]; });
  next.mediaLibraryRef = `media-library:${item.id}`;
  next.mediaLibraryItem = { id: item.id, originalName: item.originalName, mediaType: item.mediaType, mimeType: item.mimeType, size: item.size };
  next.mimeType = item.mimeType;
  next.mediaSize = item.size;
  if (nodeType === 'image_message') next.sourceType = 'library';
  if (nodeType === 'file_document') next.fileName = config.fileName || item.originalName;
  else next.fileName = item.originalName;
  return next;
}

export function clearLibrarySelection(config, nodeType) {
  const next = { ...config };
  LIBRARY_FIELDS.forEach((field) => { delete next[field]; });
  ['mimeType', 'mediaSize'].forEach((field) => { delete next[field]; });
  if (nodeType !== 'file_document') delete next.fileName;
  if (nodeType === 'image_message') next.sourceType = 'url';
  return next;
}

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const timer = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(timer); }, [value, delay]);
  return debounced;
}

function Thumb({ item, height = 96 }) {
  const visual = item.mediaType === 'image' || item.mediaType === 'video';
  return (
    <Box sx={{ height, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', overflow: 'hidden', borderRadius: 1 }}>
      {visual
        ? <AuthenticatedMedia source={libraryMediaSource(item.id)} mediaType={item.mediaType} fileName={item.originalName} alt={item.originalName} />
        : item.mediaType === 'audio' ? <GraphicEqIcon color="action" /> : <DescriptionOutlinedIcon color="action" />}
    </Box>
  );
}

export function MediaLibraryPickerDialog({ open, mediaType, onClose, onSelect }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: false, error: '', items: [], pages: 1, total: 0 });
  const [selected, setSelected] = useState(null);
  const debouncedSearch = useDebounced(search);

  useEffect(() => { if (open) { setSearch(''); setPage(1); setSelected(null); } }, [open]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);
  useEffect(() => {
    if (!open || !mediaType) return undefined;
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: '' }));
    listLibraryMedia({ type: mediaType, search: debouncedSearch || undefined, page, limit: PAGE_SIZE }, { signal: controller.signal })
      .then((response) => {
        const data = response?.data?.data || {};
        setState({ loading: false, error: '', items: data.items || [], pages: data.pages || 1, total: data.total || 0 });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const status = error?.response?.status;
        setState({ loading: false, items: [], pages: 1, total: 0, error: status === 403 ? 'You do not have access to the Media Library.' : (error?.response?.data?.message || 'The Media Library could not be loaded.') });
      });
    return () => controller.abort();
  }, [open, mediaType, debouncedSearch, page]);

  const problem = selected ? libraryIncompatibility(selected, mediaType) : '';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="media-library-picker-title">
      <DialogTitle id="media-library-picker-title">Select from Media Library</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="Search by file name" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth inputProps={{ maxLength: 100 }} />
            <Chip label={`Showing ${TYPE_LABELS[mediaType] || 'files'} only`} />
          </Stack>
          {state.error && <Alert severity="error">{state.error}</Alert>}
          {state.loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading files…</Typography></Stack>}
          {!state.loading && !state.error && !state.items.length && (
            <Typography color="text.secondary">{debouncedSearch ? 'No files match your search.' : `No ${TYPE_LABELS[mediaType] || 'files'} in the Media Library yet.`}</Typography>
          )}
          <Grid container spacing={1.5}>
            {state.items.map((item) => {
              const reason = libraryIncompatibility(item, mediaType);
              const active = selected?.id === item.id;
              return (
                <Grid item xs={6} sm={4} md={3} key={item.id}>
                  <Card variant="outlined" sx={{ borderColor: active ? 'primary.main' : 'divider', borderWidth: active ? 2 : 1, opacity: reason ? 0.55 : 1 }}>
                    <CardActionArea onClick={() => setSelected(item)} aria-pressed={active} aria-label={`Choose ${item.originalName}`} sx={{ p: 1 }}>
                      <Thumb item={item} />
                      <Typography variant="body2" fontWeight={700} noWrap title={item.originalName} sx={{ mt: 0.75 }}>{item.originalName}</Typography>
                      <Typography variant="caption" color={reason ? 'error' : 'text.secondary'}>{reason || formatBytes(item.size)}</Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {state.pages > 1 && <Pagination count={state.pages} page={page} onChange={(event, value) => setPage(value)} sx={{ alignSelf: 'center' }} />}
          {selected && (
            <Stack spacing={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }} data-testid="library-preview">
              <Typography fontWeight={800}>Preview</Typography>
              {selected.mediaType === 'image' || selected.mediaType === 'video' || selected.mediaType === 'audio'
                ? <Box sx={{ maxWidth: 360 }}><AuthenticatedMedia source={libraryMediaSource(selected.id)} mediaType={selected.mediaType} fileName={selected.originalName} alt={selected.originalName} /></Box>
                : <Chip icon={<DescriptionOutlinedIcon />} label={selected.originalName} sx={{ alignSelf: 'flex-start' }} />}
              <Typography variant="body2" color="text.secondary">{selected.originalName} · {selected.mimeType} · {formatBytes(selected.size)}</Typography>
              {problem && <Alert severity="warning">{problem} Choose another file.</Alert>}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selected || Boolean(problem)} onClick={() => onSelect(selected)}>Use this file</Button>
      </DialogActions>
    </Dialog>
  );
}

// Shows the node's current library file (or a button to pick one), with Replace/Clear.
export function LibraryMediaSelection({ nodeType, config, onChange, error }) {
  const [open, setOpen] = useState(false);
  const mediaType = LIBRARY_NODE_TYPES[nodeType];
  const item = config.mediaLibraryRef ? config.mediaLibraryItem : null;
  const choose = (picked) => { setOpen(false); onChange(applyLibrarySelection(config, picked, nodeType)); };
  return (
    <Stack spacing={1}>
      {config.mediaLibraryRef ? (
        <Stack direction="row" spacing={1.5} alignItems="center" data-testid="library-selection">
          {item && <Box sx={{ width: 88, flexShrink: 0 }}><Thumb item={item} height={64} /></Box>}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={700} noWrap title={item?.originalName}>{item?.originalName || 'Media Library file'}</Typography>
            <Typography variant="caption" color="text.secondary">From Media Library{item?.size ? ` · ${formatBytes(item.size)}` : ''}</Typography>
          </Box>
          <Button size="small" onClick={() => setOpen(true)}>Replace</Button>
          <Button size="small" color="error" onClick={() => onChange(clearLibrarySelection(config, nodeType))}>Clear</Button>
        </Stack>
      ) : (
        <Button variant="outlined" startIcon={<PhotoLibraryOutlinedIcon />} onClick={() => setOpen(true)} sx={{ alignSelf: 'flex-start' }}>Select from Media Library</Button>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="caption" color="text.secondary">
        Library files are reused without re-uploading. They are sent through this flow&apos;s WhatsApp account when the flow runs.
      </Typography>
      <MediaLibraryPickerDialog open={open} mediaType={mediaType} onClose={() => setOpen(false)} onSelect={choose} />
    </Stack>
  );
}
